<template>
    <div
        class="bg-gray-200 rounded-md inline-block"
        :class="{
            'opacity-50 cursor-not-allowed': props.disabled,
            'p-0.5': size === 'sm',
            'p-1': size === 'default',
        }"
    >
        <ToggleGroupRoot
            type="single"
            :model-value="String(props.modelValue)"
            :disabled="props.disabled"
            class="grid relative overflow-hidden"
            :style="{ gridTemplateColumns: `repeat(${props.options.length}, minmax(0, 1fr))` }"
            @update:model-value="onUpdate"
        >
            <div
                class="absolute top-0 left-0 h-full rounded-md bg-primary-800 transition-transform duration-300"
                :style="{ width: `${100 / props.options.length}%`, transform: `translateX(${selectedIndex * 100}%)` }"
                aria-hidden="true"
            ></div>
            <ToggleGroupItem
                v-for="option in props.options"
                :key="String(option.value)"
                :value="String(option.value)"
                class="relative h-full border-0 transition-colors duration-300 focus:outline-none font-medium whitespace-nowrap disabled:cursor-not-allowed"
                :class="{
                    'text-white': option.value === props.modelValue,
                    'px-2 py-1 text-sm': size === 'sm',
                    'px-4 py-2': size === 'default',
                }"
            >
                {{ option.label }}
            </ToggleGroupItem>
        </ToggleGroupRoot>
    </div>
</template>

<script setup lang="ts" generic="T extends string | number">
import { computed } from 'vue';
import { ToggleGroupItem, ToggleGroupRoot } from 'radix-vue';

// Declared inline and without withDefaults: vue-tsc cannot see a generic component's props through an interface that mentions T, or through withDefaults.
const props = defineProps<{
    modelValue: T;
    options: { value: T; label: string }[];
    disabled?: boolean;
    size?: 'default' | 'sm';
}>();

const size = computed(() => props.size ?? 'default');

const emit = defineEmits<{
    'update:modelValue': [value: T];
}>();

const selectedIndex = computed(() => {
    const index = props.options.findIndex((option) => option.value === props.modelValue);

    return index === -1 ? 0 : index;
});

function onUpdate(value: string | string[] | undefined) {
    // Radix emits an empty value when the active item is clicked again; a selection is always required here, so ignore it.
    const selected = props.options.find((option) => String(option.value) === value);

    if (selected && selected.value !== props.modelValue) {
        emit('update:modelValue', selected.value);
    }
}
</script>
